/**
 * @fileoverview Central URL configuration for the PostBrix marketing repository.
 * All URLs in the project are configured via environment variables with sensible defaults.
 *
 * Environment Variables:
 *   NEXT_PUBLIC_SITE_URL / NEXT_PUBLIC_LANDING_URL → Landing page & public marketing routes (default: https://postbrix.com)
 *   NEXT_PUBLIC_APP_URL                             → Authenticated builder & dashboard (default: https://app.postbrix.com)
 *   NEXT_PUBLIC_API_URL                             → External API routes (default: https://api.postbrix.com)
 *   NEXT_PUBLIC_STATUS_URL                          → System status page (default: https://status.postbrix.com)
 */

/** Landing page / public marketing site URL */
export const SITE_URL = (
  process.env.NEXT_PUBLIC_SITE_URL ??
  process.env.NEXT_PUBLIC_LANDING_URL ??
  'https://postbrix.com'
).replace(/\/+$/, '');

/** Authenticated app / builder / dashboard (app.postbrix.com) */
export const APP_URL = (
  process.env.NEXT_PUBLIC_APP_URL ??
  'https://app.postbrix.com'
).replace(/\/+$/, '');

/** REST API base URL (api.postbrix.com) */
export const API_URL = (
  process.env.NEXT_PUBLIC_API_URL ||
  'https://api.postbrix.com'
).replace(/\/+$/, '');

/** System status page URL */
export const STATUS_URL = (
  process.env.NEXT_PUBLIC_STATUS_URL ??
  'https://status.postbrix.com'
).replace(/\/+$/, '');

/**
 * Build an absolute URL for a public site path like `/pricing`.
 *
 * @example
 *   siteUrl('/pricing') // → "https://postbrix.com/pricing"
 */
export function siteUrl(path: string = ''): string {
  if (!path) return SITE_URL;
  const normalizedPath = path.startsWith('/') ? path : '/' + path;
  return `${SITE_URL}${normalizedPath}`;
}

/**
 * Build an absolute URL for an app path like `/dashboard` or `/editor/new`.
 *
 * @example
 *   appUrl('/editor/new') // → "https://app.postbrix.com/editor/new"
 */
export function appUrl(path: string = ''): string {
  if (!path) return APP_URL;
  const normalizedPath = path.startsWith('/') ? path : '/' + path;
  return `${APP_URL}${normalizedPath}`;
}

/**
 * Build an absolute URL for an API path like `/api/templates`.
 * Always returns an absolute URL pointing to the external API domain.
 *
 * @example
 *   apiUrl('/api/templates') // → "https://api.postbrix.com/api/templates"
 */
export function apiUrl(path: string = ''): string {
  if (!path) return API_URL;
  const normalizedPath = path.startsWith('/') ? path : '/' + path;
  return `${API_URL}${normalizedPath}`;
}

